import isEqual from 'lodash.isequal'
import { createSelectorCreator, defaultMemoize } from 'reselect'
import type { CollectionNewCount, CollectionRef, Notification, NotificationCategory, NotificationsState } from './types'

const createSelector = createSelectorCreator(defaultMemoize, {
  equalityCheck: isEqual,
  maxSize: 20
})

// Notification categories whose source_id is a user_saved_search id (saved-search
// collections); the 'dashboard' category's source_id is the dashboard/collection id.
export const SAVED_SEARCH_CATEGORIES: NotificationCategory[] = [
  'saved_search_citations',
  'saved_search_publications',
  'citation_statements'
]

// Whether a single count entry (from GET /notifications/feed/collection-counts) belongs to a
// collection. A collection is tracked by a `dashboard` alert (source_id = the collection/dashboard
// id) and, when it's built from a saved search, may also have saved-search alerts (source_id =
// savedSearchId). Match EITHER, regardless of the collection's query type — a saved-search
// collection still gets a `dashboard` citation alert keyed by its dashboard id, so branching on
// queryType would miss it.
export const matchesCollection = (count?: CollectionNewCount | null, collection?: CollectionRef | null) => {
  if (!count || !collection) {
    return false
  }
  if (count.category === 'dashboard') {
    // Prefer matching on the numeric collection id; fall back to the trailing hashid of
    // the slug (collection slugs are either `<hashid>` or `<name>-<hashid>`).
    return collection.id != null
      ? String(count.sourceId) === String(collection.id)
      : !!count.slug && collection.slug?.split('-').pop() === count.slug
  }
  const savedSearchId = collection.doiQuery?.query?.savedSearchId
  return (
    savedSearchId != null &&
    SAVED_SEARCH_CATEGORIES.includes(count.category) &&
    String(count.sourceId) === String(savedSearchId)
  )
}

export interface CollectionNewActivity {
  citations: number
  papers: number
  total: number
  hasDetail: boolean
}

// The new activity belonging to a collection, from GET /notifications/feed/collection-counts.
// `newCitationCount`/`newPaperCount` are the citations and papers inside the collection's NEW
// digests; `newCount` counts digest envelope rows, so it's only a deploy-window fallback.
// Returns a fresh object — don't call this inside useSelector.
export const getCollectionNewActivity = (
  collection?: CollectionRef | null,
  counts?: CollectionNewCount[] | null
): CollectionNewActivity => {
  if (!collection || !Array.isArray(counts) || counts.length === 0) {
    return { citations: 0, papers: 0, total: 0, hasDetail: false }
  }
  const matching = counts.filter(count => matchesCollection(count, collection))
  const citations = matching.reduce((sum, count) => sum + (count.newCitationCount || 0), 0)
  const papers = matching.reduce((sum, count) => sum + (count.newPaperCount || 0), 0)
  const rows = matching.reduce((sum, count) => sum + (count.newCount || 0), 0)
  const hasDetail = matching.some(count => count.newCitationCount != null || count.newPaperCount != null)
  return { citations, papers, total: hasDetail ? citations + papers : rows, hasDetail }
}

// Total new activity for a collection — what the /collections card badge displays.
export const getCollectionNewCount = (collection?: CollectionRef | null, counts?: CollectionNewCount[] | null) =>
  getCollectionNewActivity(collection, counts).total

// Resolve the notification (subscription) ids that belong to a collection, from the list
// returned by GET /notifications/notifications (snake_case `source_id`/`category`/`id`).
// Used to scope the feed to a single collection via filters.notificationIDs.
export const getCollectionNotificationIds = (
  collection?: CollectionRef | null,
  notifications?: Notification[] | null
) => {
  if (!collection || !Array.isArray(notifications) || notifications.length === 0) {
    return []
  }

  const savedSearchId = collection.doiQuery?.query?.savedSearchId

  return notifications
    .filter(notification => {
      // A collection's citation/publication alert is a `dashboard` notification keyed by the
      // collection id — true even for saved-search-backed collections. Also accept saved-search
      // alerts keyed by savedSearchId. Matching either (not branching on queryType) is what keeps
      // the feed from silently disappearing on saved-search collections.
      const dashboardMatch =
        notification.category === 'dashboard' &&
        collection.id != null &&
        String(notification.source_id) === String(collection.id)
      const savedSearchMatch =
        savedSearchId != null &&
        SAVED_SEARCH_CATEGORIES.includes(notification.category) &&
        String(notification.source_id) === String(savedSearchId)
      return dashboardMatch || savedSearchMatch
    })
    .map(notification => notification.id)
}

// Takes the notifications slice, not RootState — the feed components select the
// slice first and pass it in.
export const selectFilteredNotificationItems = createSelector(
  (state: NotificationsState) => state.notificationItems,
  (state: NotificationsState) => state.notificationItemsFilters,
  (notificationItems, notificationItemsFilters) =>
    (notificationItems || []).filter(notificationItem => {
      // if archived is null we also filter them out
      if (notificationItem.status === 'ARCHIVED' && !notificationItemsFilters?.status?.archived) {
        return false
      }
      if (
        notificationItem.status === 'NEW' &&
        typeof notificationItemsFilters?.status?.read === 'boolean' &&
        notificationItemsFilters?.status?.new === false
      ) {
        return false
      }
      if (
        notificationItem.status === 'READ' &&
        typeof notificationItemsFilters?.status?.read === 'boolean' &&
        notificationItemsFilters?.status?.read === false
      ) {
        return false
      }
      return true
    })
)
